import React from "react";
import { Send, Smile } from "lucide-react";

const AGENT_AVATAR =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_responsive-rebuild-32/artifacts/3p6lx1l3_219ai.jpg";
const REMODEL_BG =
  "https://images.unsplash.com/photo-1638799869566-b17fa794c4de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBiYXRocm9vbSUyMHJlbW9kZWx8ZW58MHx8fHwxNzg0NjcwNTE3fDA&ixlib=rb-4.1.0&q=85";

const VisitorMockup = () => {
  return (
    <div className="w-[560px] h-[720px] flex">
      {/* Browser window */}
      <div className="w-full h-full flex flex-col rounded-2xl bg-white shadow-2xl shadow-blue-900/10 border border-slate-200 overflow-hidden">
        {/* Browser chrome */}
        <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-400" />
            <span className="w-3 h-3 rounded-full bg-yellow-400" />
            <span className="w-3 h-3 rounded-full bg-green-400" />
          </div>
          <div className="mx-auto text-xs text-slate-500 bg-white border border-slate-200 rounded-md px-4 py-1 truncate max-w-[300px]">
            🔒 brighthomeremodels.com
          </div>
        </div>

        {/* Site content */}
        <div className="relative bg-white flex-1 flex flex-col">
          {/* Hero with remodel background */}
          <div
            className="relative flex-1 min-h-[560px] bg-cover bg-center"
            style={{ backgroundImage: `url(${REMODEL_BG})` }}
          >
            {/* Overlay for readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/10" />

            {/* Top nav */}
            <div className="relative flex items-center justify-between px-6 py-4">
              <div className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight">
                Bright<span className="text-blue-600">Home</span>{" "}
                <span className="text-slate-500 font-semibold text-sm align-middle">
                  Remodels
                </span>
              </div>
              <div className="hidden sm:flex items-center gap-5 text-xs font-semibold text-slate-700">
                <span>Services</span>
                <span>Projects</span>
                <span>About</span>
                <span>Contact</span>
              </div>
              <div className="sm:hidden space-y-1">
                <span className="block w-5 h-[2px] bg-slate-800" />
                <span className="block w-5 h-[2px] bg-slate-800" />
                <span className="block w-5 h-[2px] bg-slate-800" />
              </div>
            </div>

            {/* Hero text */}
            <div className="relative px-6 pt-6 pb-10 max-w-[60%]">
              <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-blue-600 bg-blue-100/80 rounded-full px-2.5 py-1">
                Kitchen &amp; Bath Experts
              </span>
              <h3 className="mt-3 text-3xl sm:text-4xl font-extrabold text-slate-900 leading-[1.1]">
                Beautiful <span className="text-blue-600">bathroom</span>
                <br />&amp; kitchen remodels
              </h3>
              <p className="mt-3 text-sm text-slate-700 max-w-[300px]">
                Transform your home with our award-winning design &amp;
                installation team. Trusted by 500+ happy homeowners.
              </p>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button className="bg-blue-600 hover:bg-blue-700 transition-colors text-white text-sm font-bold rounded-md px-4 py-2.5">
                  Get a Free Quote
                </button>
                <button className="text-slate-800 border border-slate-300 hover:bg-white text-sm font-bold rounded-md px-4 py-2.5 bg-white/70">
                  View Projects
                </button>
              </div>

              {/* Small stat row */}
              <div className="mt-5 flex items-center gap-4 text-[11px] text-slate-700">
                <div>
                  <div className="font-extrabold text-slate-900 text-base">
                    500+
                  </div>
                  <div>Happy Homes</div>
                </div>
                <div>
                  <div className="font-extrabold text-slate-900 text-base">
                    4.9★
                  </div>
                  <div>Rating</div>
                </div>
                <div>
                  <div className="font-extrabold text-slate-900 text-base">
                    12yrs
                  </div>
                  <div>Experience</div>
                </div>
              </div>
            </div>
          </div>

          {/* Chat widget overlay */}
          <div className="absolute right-4 bottom-4 sm:right-6 sm:bottom-6 w-[280px] sm:w-[310px] rounded-2xl bg-white shadow-2xl shadow-blue-900/25 border border-slate-200 overflow-hidden">
            {/* Widget header */}
            <div className="flex items-center justify-between px-3 py-2.5 bg-white border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div
                    className="w-9 h-9 rounded-full bg-cover ring-2 ring-white shadow-sm"
                    style={{
                      backgroundImage: `url(${AGENT_AVATAR})`,
                      backgroundPosition: "center 20%",
                      backgroundSize: "140%",
                    }}
                  />
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-white" />
                </div>
                <div className="leading-tight">
                  <div className="text-xs font-bold text-slate-900">Sarah</div>
                  <div className="text-[10px] text-green-600 flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-green-500" />
                    Live
                  </div>
                </div>
              </div>
              <button className="text-slate-400 hover:text-slate-600 text-base leading-none">
                ×
              </button>
            </div>

            {/* Messages */}
            <div className="px-3 py-3 space-y-2 bg-slate-50/60">
              <div className="flex items-start">
                <div className="bg-white rounded-xl rounded-tl-sm px-3 py-1.5 text-[11px] text-slate-800 shadow-sm max-w-[90%]">
                  Hey, it&rsquo;s Sarah
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-white rounded-xl rounded-tl-sm px-3 py-1.5 text-[11px] text-slate-800 shadow-sm max-w-[92%]">
                  I&rsquo;m here live, not an automated chatbot. How can
                  I help you?
                </div>
              </div>

              <div className="flex justify-end">
                <div className="bg-blue-600 text-white rounded-xl rounded-tr-sm px-3 py-1.5 text-[11px] shadow-sm max-w-[92%]">
                  Hi, I&rsquo;m planning a kitchen remodel, roughly 200 sq ft.
                  Could you share pricing?
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-white rounded-xl rounded-tl-sm px-3 py-1.5 text-[11px] text-slate-800 shadow-sm max-w-[94%] border border-blue-100">
                  Happy to help! May I have your name and phone number so I can send you the quote?
                </div>
              </div>

              <div className="flex justify-end">
                <div className="bg-blue-600 text-white rounded-xl rounded-tr-sm px-3 py-1.5 text-[11px] shadow-sm max-w-[92%]">
                  Sure, it&rsquo;s John, and my number is 555 123 4567.
                </div>
              </div>
            </div>

            {/* Input */}
            <div className="flex items-center gap-2 px-3 py-2 border-t border-slate-100 bg-white">
              <Smile className="w-3.5 h-3.5 text-slate-400" />
              <input
                readOnly
                placeholder="Type a message..."
                className="flex-1 text-[11px] text-slate-500 placeholder:text-slate-400 bg-transparent outline-none"
              />
              <button className="w-6 h-6 rounded-md bg-blue-600 flex items-center justify-center">
                <Send className="w-3 h-3 text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorMockup;
